import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { ToolsModule } from '@huge-monorepo/shared/tools';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MaterialModule } from '@huge-monorepo/shared/modules';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { FormControl, Validators } from '@angular/forms';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipEditedEvent, MatChipInputEvent } from '@angular/material/chips';
import { SharedGenericSingletonService, SharedTeltonikaSingletonService } from '@huge-monorepo/shared/services';
import { IHIPTeltonikaService, ICustomerTeltonikaService, ICustomerGenericService, IHIPGenericService } from '@huge-monorepo/shared/interfaces';

@Component({
  selector: 'huge-monorepo-create-update-teltonika-task',
  standalone: true,
  imports: [MatCardModule, MatProgressBarModule, ToolsModule, MaterialModule, CommonModule],
  templateUrl: './create-update-teltonika-task.component.html',
  styleUrl: './create-update-teltonika-task.component.scss',
})
export class CreateUpdateTeltonikaTaskComponent implements OnInit {
  teltonikaService = this.sharedTeltonikaService.getService();
  finishedLoading: boolean = false;
  taskData: any;
  loading: boolean = false;
  TaskGroupNameControl = new FormControl('', [Validators.required]);
  TaskNameControl = new FormControl('', [Validators.required]);
  TaskTypeControl = new FormControl('', [Validators.required]);
  CommandControl = new FormControl('', [Validators.required]);
  TimeoutControl = new FormControl(null, [Validators.required]);
  StopOnFailureControl = new FormControl(false, [Validators.required]);
  genericService = this.sharedGenericSingletonService.getGenericService();

  taskTypes = ["Command"]

  readonly addOnBlur = true;
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  acceptableCodes: number[] = [0];
  acceptableCodesError: string | null;

  constructor(
    private sharedTeltonikaService: SharedTeltonikaSingletonService<ICustomerTeltonikaService | IHIPTeltonikaService>,
    private route: ActivatedRoute,
    private navRoute: Router,
    private sharedGenericSingletonService: SharedGenericSingletonService<ICustomerGenericService | IHIPGenericService>
  ) { }

  ngOnInit() {
    this.route.paramMap.subscribe((params: ParamMap) => {
      let id = parseInt(params.get('id') || '', 10);
      if (!isNaN(id) && id > 0) {
        this.teltonikaService.getTeltonikaTask(id).subscribe((data) => {
          this.taskData = data;

          // Assign previous values to populate formcontrols
          this.TaskGroupNameControl.setValue(this.taskData.GroupName);
          this.TaskNameControl.setValue(this.taskData.TaskName);
          this.TaskTypeControl.setValue(this.taskData.TaskType);
          this.CommandControl.setValue(this.taskData.Commands.join("\n"));
          this.TimeoutControl.setValue(this.taskData.Timeout);
          this.acceptableCodes = this.taskData.AcceptableCodes;
          this.StopOnFailureControl.setValue(this.taskData.StopOnFailure);

          this.finishedLoading = true;
        });
      } else {
        // TODO: implement empty form groups here
        this.finishedLoading = true;
      }
    });
  }

  updateStopOnFailureControl(event: any) {
    this.StopOnFailureControl.setValue(event.checked);
  }

  // checkAddAcceptableCode(event: KeyboardEvent) {
  //   if (event.key === 'Enter' || event.key === ',') {
  //     this.acceptableCodes.push(parseInt(this.AcceptableCodesControl.value));
  //     this.AcceptableCodesControl.setValue('');
  //     console.log(this.acceptableCodes);
  //   }
  // }

  addAcceptableCode(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    // Add code
    if (value) {
      const parsedVal = parseInt(value);
      if (isNaN(parsedVal)) {
        this.genericService.createToast('Invalid code', 'The code must be a number', 'error');
        return;
      }
      if (this.acceptableCodes.includes(parsedVal)) {
        this.genericService.createToast('Duplicate code', 'The code is already in the list', 'error');
        return;
      }
      this.acceptableCodes.push(parsedVal);
      this.acceptableCodesError = null;
    }

    // Clear the input value
    event.chipInput!.clear();
  }

  removeAcceptableCode(code: any): void {
    const index = this.acceptableCodes.indexOf(code);

    if (index >= 0) {
      this.acceptableCodes.splice(index, 1);
      this.acceptableCodes.length == 0 ? this.acceptableCodesError = 'At least one acceptable code is required' : this.acceptableCodesError = null;
    }
  }

  editAcceptableCodes(code: any, event: MatChipEditedEvent) {
    const value = event.value.trim();

    // Remove code if it is now empty
    if (!value) {
      this.removeAcceptableCode(code);
      this.acceptableCodes.length == 0 ? this.acceptableCodesError = 'At least one acceptable code is required' : this.acceptableCodesError = null;
      return;
    }

    // Edit existing code
    const index = this.acceptableCodes.indexOf(code);
    if (index >= 0) {
      const parsedVal = parseInt(value);
      if (isNaN(parsedVal)) {
        this.genericService.createToast('Invalid code', 'The code must be a number', 'error');
        return;
      }
      this.acceptableCodes[index] = parsedVal;
    }
  }

  submitTask() {
    // Check that there are acceptable codes
    if (this.acceptableCodes.length == 0) {
      this.acceptableCodesError = 'At least one acceptable code is required';
      return;
    }

    let commandsArray = this.CommandControl.value ? this.CommandControl.value.split("\n") : [];
    // Remove empty lines
    for (let i = 0; i < commandsArray.length; i++) {
      commandsArray[i] = commandsArray[i].trim();
      if (commandsArray[i] == "") {
        commandsArray.splice(i, 1);
        i--;
      }
    }
    if (commandsArray.length == 0) {
      this.CommandControl.setErrors({ required: true });
      return;
    }

    if (this.taskData == null) {
      this.taskData = {};
    }

    this.taskData.GroupName = this.TaskGroupNameControl.value;
    this.taskData.TaskName = this.TaskNameControl.value;
    this.taskData.TaskType = this.TaskTypeControl.value;
    this.taskData.Commands = commandsArray;
    this.taskData.Timeout = parseInt(this.TimeoutControl.value || '0');
    this.taskData.AcceptableCodes = this.acceptableCodes;
    this.taskData.StopOnFailure = this.StopOnFailureControl.value;

    this.teltonikaService.upsertTeltonikaTask(this.taskData).subscribe({
      next: (data) => {
        this.genericService.createToast('Task saved successfully', 'The task has been saved successfully', 'success');
        this.loading = true;
      },
      complete: () => {
        this.navRoute.navigate(['/Teltonika/Tasks']);
      }
    });
  }

  cancel() {
    this.navRoute.navigate(['/Teltonika/Tasks']);
  }

}
